/* Hope & Glory Trade — theme.js
   Lightweight, dependency-free. Custom elements + a few document-level
   behaviours. Safe under Shopify Theme Editor section reloads. */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Reveal on scroll                                                    */
  /* ------------------------------------------------------------------ */
  var revealObserver = null;
  function initReveals(root) {
    var els = (root || document).querySelectorAll('.reveal:not(.visible)');
    if (!els.length) return;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('visible'); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12 });
    }
    els.forEach(function (el) { revealObserver.observe(el); });
  }

  /* ------------------------------------------------------------------ */
  /* Focus trap helper                                                   */
  /* ------------------------------------------------------------------ */
  function trapFocus(container, e) {
    var focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Drawer base (menu + cart)                                           */
  /* ------------------------------------------------------------------ */
  function makeDrawer(el) {
    var opener = null;
    var drawer = {
      el: el,
      open: function (trigger) {
        opener = trigger || document.activeElement;
        el.classList.add('open');
        el.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        var target = el.querySelector('[data-autofocus]') || el.querySelector('button, a[href], input');
        if (target) window.setTimeout(function () { target.focus(); }, 60);
      },
      close: function () {
        el.classList.remove('open');
        el.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        if (opener && opener.focus) opener.focus();
        opener = null;
      },
      isOpen: function () { return el.classList.contains('open'); }
    };
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.preventDefault(); drawer.close(); }
      if (e.key === 'Tab' && drawer.isOpen()) trapFocus(el, e);
    });
    el.querySelectorAll('[data-drawer-close]').forEach(function (btn) {
      btn.addEventListener('click', function () { drawer.close(); });
    });
    return drawer;
  }

  var menuDrawer = null;
  function initMenuDrawer() {
    var el = document.getElementById('MenuDrawer');
    if (!el) return;
    menuDrawer = makeDrawer(el);
    document.querySelectorAll('[data-menu-open]').forEach(function (btn) {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', function () {
        menuDrawer.open(btn);
        btn.setAttribute('aria-expanded', 'true');
      });
    });
    // Close the drawer when a link inside is used (anchor links on same page).
    el.querySelectorAll('a[href]').forEach(function (a) {
      a.addEventListener('click', function () { menuDrawer.close(); });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Cart                                                                */
  /* ------------------------------------------------------------------ */
  var cartDrawer = null;

  function initCartDrawer() {
    var el = document.getElementById('CartDrawer');
    if (!el) return;
    cartDrawer = makeDrawer(el);
    bindCartTriggers();
    bindCartLines(el);
  }

  function bindCartTriggers() {
    document.querySelectorAll('[data-cart-open]').forEach(function (btn) {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', function (e) {
        if (!cartDrawer) return; // fall through to /cart page
        e.preventDefault();
        cartDrawer.open(btn);
      });
    });
  }

  function refreshCart(openAfter) {
    return fetch(window.location.pathname + '?sections=cart-drawer')
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var html = sections['cart-drawer'];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.getElementById('CartDrawer');
        var current = document.getElementById('CartDrawer');
        if (fresh && current) {
          var wasOpen = current.classList.contains('open');
          current.querySelector('.cart-drawer__panel').innerHTML =
            fresh.querySelector('.cart-drawer__panel').innerHTML;
          if (wasOpen) {
            current.classList.add('open');
            document.body.style.overflow = 'hidden';
          }
          bindCartLines(current);
        }
        var freshCount = doc.querySelector('[data-cart-count-source]');
        if (freshCount) {
          document.querySelectorAll('.cart-count-bubble').forEach(function (b) {
            b.textContent = freshCount.getAttribute('data-cart-count-source') > 0
              ? freshCount.getAttribute('data-cart-count-source')
              : '';
          });
        }
        if (openAfter && cartDrawer) cartDrawer.open();
      });
  }

  function bindCartLines(scope) {
    scope.querySelectorAll('[data-line-qty]').forEach(function (wrap) {
      if (wrap.dataset.bound) return;
      wrap.dataset.bound = '1';
      var input = wrap.querySelector('input');
      var line = wrap.getAttribute('data-line');
      wrap.querySelectorAll('button').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var delta = btn.hasAttribute('data-qty-minus') ? -1 : 1;
          var next = Math.max(0, parseInt(input.value || '1', 10) + delta);
          changeLine(line, next, scope);
        });
      });
      input.addEventListener('change', function () {
        changeLine(line, Math.max(0, parseInt(input.value || '0', 10)), scope);
      });
    });
    scope.querySelectorAll('[data-line-remove]').forEach(function (btn) {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', function () {
        changeLine(btn.getAttribute('data-line'), 0, scope);
      });
    });
  }

  function changeLine(line, quantity, scope) {
    var drawerEl = document.getElementById('CartDrawer');
    if (drawerEl) drawerEl.setAttribute('aria-busy', 'true');
    fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: parseInt(line, 10), quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        // If we're on the cart page, reload for simplicity + accuracy.
        if (document.body.classList.contains('template-cart') && !scope.closest('#CartDrawer')) {
          window.location.reload();
          return;
        }
        return refreshCart(false);
      })
      .finally(function () {
        if (drawerEl) drawerEl.removeAttribute('aria-busy');
      });
  }

  /* ------------------------------------------------------------------ */
  /* Product forms (add to cart via AJAX, open drawer)                   */
  /* ------------------------------------------------------------------ */
  function initProductForms(root) {
    (root || document).querySelectorAll('form[data-product-form]').forEach(function (form) {
      if (form.dataset.bound) return;
      form.dataset.bound = '1';
      form.addEventListener('submit', function (e) {
        if (!cartDrawer) return; // no drawer: allow normal POST to /cart/add
        e.preventDefault();
        var btn = form.querySelector('[data-add-to-cart]');
        var errEl = form.querySelector('[data-form-error]');
        if (errEl) { errEl.hidden = true; errEl.textContent = ''; }
        if (btn) { btn.classList.add('loading'); btn.setAttribute('aria-disabled', 'true'); }
        var data = new FormData(form);
        fetch(window.routes.cart_add_url + '.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: data
        })
          .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
          .then(function (res) {
            if (!res.ok) {
              var msg = (res.json && (res.json.description || res.json.message)) || window.themeStrings.cartError;
              if (errEl) { errEl.textContent = msg; errEl.hidden = false; }
              return;
            }
            return refreshCart(true);
          })
          .catch(function () {
            if (errEl) { errEl.textContent = window.themeStrings.cartError; errEl.hidden = false; }
          })
          .finally(function () {
            if (btn) { btn.classList.remove('loading'); btn.removeAttribute('aria-disabled'); }
          });
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Quantity inputs                                                     */
  /* ------------------------------------------------------------------ */
  function initQtyInputs(root) {
    (root || document).querySelectorAll('[data-qty]').forEach(function (wrap) {
      if (wrap.dataset.bound || wrap.hasAttribute('data-line-qty')) return;
      wrap.dataset.bound = '1';
      var input = wrap.querySelector('input');
      wrap.querySelectorAll('button').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var delta = btn.hasAttribute('data-qty-minus') ? -1 : 1;
          var min = parseInt(input.min || '1', 10);
          input.value = Math.max(min, parseInt(input.value || '1', 10) + delta);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Purchase options (one-time vs selling plan) + variant selects       */
  /* Prices come from Liquid-rendered data attributes — never hard-coded */
  /* ------------------------------------------------------------------ */
  function initPurchaseOptions(root) {
    (root || document).querySelectorAll('[data-purchase-options]').forEach(function (box) {
      if (box.dataset.bound) return;
      box.dataset.bound = '1';
      var form = document.getElementById(box.getAttribute('data-form-id'));
      if (!form) form = box.closest('form');
      var priceTargets = document.querySelectorAll('[data-price-for="' + box.getAttribute('data-form-id') + '"]');

      function apply(input) {
        box.querySelectorAll('.purchase-option').forEach(function (label) {
          label.setAttribute('data-selected', label.contains(input) ? 'true' : 'false');
        });
        var planInput = form ? form.querySelector('input[name="selling_plan"]') : null;
        if (planInput) planInput.value = input.getAttribute('data-selling-plan') || '';
        priceTargets.forEach(function (target) {
          var now = target.querySelector('[data-price-now]');
          var was = target.querySelector('[data-price-was]');
          var note = target.querySelector('[data-price-note]');
          if (now && input.getAttribute('data-price')) now.textContent = input.getAttribute('data-price');
          if (was) {
            var w = input.getAttribute('data-compare');
            was.textContent = w || '';
            was.hidden = !w;
          }
          if (note) {
            var n = input.getAttribute('data-note');
            note.textContent = n || '';
            note.hidden = !n;
          }
        });
      }

      box.querySelectorAll('input[type="radio"]').forEach(function (input) {
        input.addEventListener('change', function () { apply(input); });
      });
      var checked = box.querySelector('input[type="radio"]:checked');
      if (checked) apply(checked);
    });
  }

  function initVariantSelects(root) {
    (root || document).querySelectorAll('[data-variant-selects]').forEach(function (wrap) {
      if (wrap.dataset.bound) return;
      wrap.dataset.bound = '1';
      var formId = wrap.getAttribute('data-form-id');
      var form = document.getElementById(formId);
      var dataEl = document.getElementById('VariantData-' + wrap.getAttribute('data-section'));
      if (!dataEl || !form) return;
      var variants = JSON.parse(dataEl.textContent);

      function currentVariant() {
        var selected = [];
        wrap.querySelectorAll('select').forEach(function (s) { selected.push(s.value); });
        return variants.find(function (v) {
          return v.options.every(function (opt, i) { return opt === selected[i]; });
        });
      }

      wrap.querySelectorAll('select').forEach(function (s) {
        s.addEventListener('change', function () {
          var v = currentVariant();
          var idInput = form.querySelector('input[name="id"]');
          var btn = form.querySelector('[data-add-to-cart]');
          var label = btn ? btn.querySelector('.btn-label') : null;
          var priceBox = document.querySelector('[data-price-for="' + formId + '"]');
          if (!v) {
            if (btn) { btn.disabled = true; }
            if (label) label.textContent = window.themeStrings.unavailable;
            return;
          }
          idInput.value = v.id;
          if (btn) btn.disabled = !v.available;
          if (label) label.textContent = v.available ? window.themeStrings.addToCart : window.themeStrings.soldOut;
          if (priceBox) {
            var now = priceBox.querySelector('[data-price-now]');
            var was = priceBox.querySelector('[data-price-was]');
            if (now) now.textContent = v.price_formatted;
            if (was) {
              was.textContent = v.compare_formatted || '';
              was.hidden = !v.compare_formatted;
            }
          }
          var url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', url.toString());
        });
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Product gallery thumbs                                              */
  /* ------------------------------------------------------------------ */
  function initGalleries(root) {
    (root || document).querySelectorAll('[data-gallery]').forEach(function (gallery) {
      if (gallery.dataset.bound) return;
      gallery.dataset.bound = '1';
      var main = gallery.querySelector('[data-gallery-main] img');
      gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (!main) return;
          main.src = btn.getAttribute('data-src');
          if (btn.getAttribute('data-srcset')) main.srcset = btn.getAttribute('data-srcset');
          main.alt = btn.getAttribute('data-alt') || '';
          gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (b) {
            b.setAttribute('aria-current', b === btn ? 'true' : 'false');
          });
        });
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Pathway → enquiry interest tagging                                  */
  /* Links carry data-interest; the enquiry form has a hidden-ish select */
  /* ------------------------------------------------------------------ */
  function setInterest(value) {
    var field = document.querySelector('[data-interest-field]');
    var chipWrap = document.querySelector('[data-interest-chip]');
    if (field) field.value = value;
    if (chipWrap) {
      chipWrap.innerHTML = '';
      if (value) {
        var chip = document.createElement('span');
        chip.className = 'chip';
        chip.textContent = value;
        chipWrap.appendChild(chip);
      }
    }
  }

  function initInterestRouting() {
    document.querySelectorAll('[data-interest]').forEach(function (link) {
      if (link.dataset.bound) return;
      link.dataset.bound = '1';
      link.addEventListener('click', function () {
        setInterest(link.getAttribute('data-interest'));
      });
    });
    // Also honour ?interest= in the URL (e.g. arriving from another page)
    var params = new URLSearchParams(window.location.search);
    if (params.get('interest')) setInterest(params.get('interest'));
  }

  /* ------------------------------------------------------------------ */
  /* Boot + Theme Editor support                                         */
  /* ------------------------------------------------------------------ */
  function initAll(root) {
    initReveals(root);
    initMenuDrawer();
    initCartDrawer();
    initProductForms(root);
    initQtyInputs(root);
    initPurchaseOptions(root);
    initVariantSelects(root);
    initGalleries(root);
    initInterestRouting();
    bindCartTriggers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function () {
    document.body.style.overflow = '';
  });
})();
